import cn from 'classnames';
import { useState, useEffect } from 'react';
import { BsBriefcaseFill } from 'react-icons/bs';
import { FaHome } from 'react-icons/fa';
import { MdLocationPin } from 'react-icons/md';
import Cookie from 'universal-cookie';

import { createAddressUser } from '../../api/wave/users';
import { ValuesToBeUpdated } from '../../store/cart';
import {
  TakeAwayTimeslotsFetched,
  ExpressTimeslotsFetched,
  TakeAwayTimeslotsStore,
  ExpressTimeslotsStore,
} from '../../store/timeslots';
import User from '../../store/user';
import { getAddressTypeInfo } from '../../utils/addressUtilities';
import { addToAnalytics } from '../../utils/analytics';
import { checkPostalCodeForMasoutis } from '../../utils/masoutisCheck';
import AddressViewModal from '../AddressViewModal/AddressViewModal';
import { SecondaryButton } from '../Buttons/Buttons';

import styles from './AddressSelection.module.scss';

export default function AddressSelection({ setIsOpen }) {
  const [openAddressModal, setOpenAddressModal] = useState(false);
  const [user, setUser] = useState(undefined);
  const cookies = new Cookie();
  const token = cookies.get('token');

  useEffect(() => {
    const user$ = User.subscribe(_user => {
      if (!_user) return;
      const myUser = {
        ..._user,
        address: [_user.mainAddress, ...sortAddresses(_user)],
      };
      setUser(myUser);
    });
    return () => {
      user$.unsubscribe();
    };
  }, []);

  function sortAddresses(_user) {
    return _user.address.filter(address => _user?.mainAddress?._id !== address._id);
  }

  async function addAddress(newAddress) {
    addToAnalytics('addAddres', undefined, [['option', 'submit']]);

    setOpenAddressModal(false); // close the add-address form

    const isMasoutis = await checkPostalCodeForMasoutis(
      newAddress.postalCode,
      () => setIsOpen(true) // reopen the address list when user clicks "Επιλέξτε άλλη διεύθυνση"
    );
    if (isMasoutis) {
      setIsOpen(false); // close the address list — Masoutis alert is now the only modal
      return;
    }

    const createdAddress = { address: [{ ...newAddress }] };
    const response = await createAddressUser(token, createdAddress);
    if (typeof response === 'string') {
      setOpenAddressModal(false);
      return;
    }
    User.addAddress(response?.address);

    setOpenAddressModal(false);
  }

  async function onClickGetAddress(index) {
    const selectedAddress = user.address[index];

    const isMasoutis = await checkPostalCodeForMasoutis(
      selectedAddress.postalCode,
      () => setIsOpen(true) // reopen the address list when user clicks "Επιλέξτε άλλη διεύθυνση"
    );
    if (isMasoutis) {
      setIsOpen(false); // close the address list — Masoutis alert is now the only modal
      return;
    }

    TakeAwayTimeslotsStore.update([]);
    ExpressTimeslotsStore.update([]);
    TakeAwayTimeslotsFetched.update(false);
    ExpressTimeslotsFetched.update(false);
    const prevMainAddress = User.getUserMainAddress();
    ValuesToBeUpdated.updateAddress(prevMainAddress);
    User.updateMainAddress(selectedAddress);
    setIsOpen(false);
  }

  function getAddressTypeIcon(type, index) {
    switch (type) {
      case 'home':
        return <FaHome className={cn(styles.addressIcon, { [styles.addressIconSelected]: index === 0 })} alt={type} />;
      case 'work':
        return (
          <BsBriefcaseFill
            className={cn(styles.addressIcon, { [styles.addressIconSelected]: index === 0 })}
            alt={type}
          />
        );
      default:
        return (
          <MdLocationPin className={cn(styles.addressIcon, { [styles.addressIconSelected]: index === 0 })} alt={type} />
        );
    }
  }

  if (!user?._id) return null;

  return (
    <div className={styles.addressContainer}>
      <div className={styles.addressListContainer}>
        <ul className={styles.addresList}>
          {user?.address?.map((a, index) => (
            <li key={a._id} className={styles.addressItem}>
              <button className={styles.addressInfo} type="button" onClick={() => onClickGetAddress(index)}>
                <div className={styles.addressInfoContainer}>
                  {getAddressTypeIcon(a.type, index)}
                  <div>
                    <p className={cn(styles.addressType, { [styles.addressTypeSelected]: index === 0 })}>
                      {`${getAddressTypeInfo(a.type).label} - ${a.street} ${a.streetNo}`}
                    </p>
                    <p className={styles.addressDetails}>
                      {`${a.street}
                             ${a.streetNo},
                              ${a.city},
                               ${a.postalCode}`}
                    </p>
                    <p className={styles.addressDetails}>{`Όροφος ${a.floor}, ${a.nameAtBell}`}</p>
                  </div>
                </div>
              </button>
            </li>
          ))}
        </ul>
      </div>
      <div className={styles.buttonContainer}>
        <SecondaryButton onClick={() => setOpenAddressModal(true)}>
          <p className={styles.buttonText}>Προσθήκη διεύθυνσης</p>
        </SecondaryButton>
        {openAddressModal && (
          <AddressViewModal isOpen={openAddressModal} setIsOpen={setOpenAddressModal} onSubmitCB={addAddress} />
        )}
      </div>
    </div>
  );
}
